import routesConfig from './routes/routesConfig';
import { AiChatContextProvider } from '@features/AiChat/model/context';
import {
  BlockingInterfacesProvider,
  OnboardingSessionProvider,
  PwaInstallationProvider
} from '@/features/Onboarding';
import { captureUtmParams } from '@/features/Auth';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { getRouteForcedTheme } from './routes/getRouteForcedTheme';
import { initOfflineQueue } from '@/lib/offlineQueue';
import { isRecoveryRoute } from './pwa/isRecoveryRoute';
import { OrganizationProvider } from '@/features/Organization';
import { PwaUpdateLayer, PwaUpdateProvider } from '@app/pwa';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SyncIndicator } from '@/components/ui/SyncIndicator';
import { ThemeColorMeta, ThemeProvider } from '@shared/theme';
import { Toaster as SonnerToaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuthStore } from '@/features/Auth/model/authStore';
import { useEffect, useSyncExternalStore } from 'react';

const queryClient = new QueryClient();
const router = createBrowserRouter(routesConfig);
const getPathname = (): string => router.state.location.pathname;

const App = () => {
  const isOnboardingSessionResolved = useAuthStore((state) => state.initialized);
  const onboardingSessionKey = useAuthStore((state) => {
    return state.isAuthenticated ? state.tokenData?.user_id ?? null : null;
  });
  const pathname = useSyncExternalStore(router.subscribe, getPathname);

  useEffect(() => {
    // DEBUG: recover and log errors caught by ErrorBoundary in a previous session
    const storedError = sessionStorage.getItem('__error_boundary_info');
    if (storedError) {
      try {
        const info = JSON.parse(storedError);
        console.warn('[DEBUG][ErrorBoundary] previous session caught an error:', info.message);
        console.warn('[DEBUG][ErrorBoundary] stack:', info.stack);
        console.warn('[DEBUG][ErrorBoundary] timestamp:', new Date(info.time).toISOString());
      } catch {
        // ignore parse errors
      }
      sessionStorage.removeItem('__error_boundary_info');
    }

    captureUtmParams();
    useAuthStore.getState().initAuth();
    initOfflineQueue();
  }, []);

  return (
    <ThemeProvider forcedTheme={getRouteForcedTheme(pathname)}>
      <ThemeColorMeta />
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SonnerToaster position="top-center" />
          <div
            className="fixed right-3 z-[60] flex items-center gap-2"
            style={{ top: 'max(env(safe-area-inset-top, 8px), 8px)' }}
          >
            {/* <ThemeToggle size="sm" /> */}
            <SyncIndicator />
          </div>
          <PwaInstallationProvider>
            <OnboardingSessionProvider
              isSessionResolved={isOnboardingSessionResolved}
              sessionKey={onboardingSessionKey}
            >
              <BlockingInterfacesProvider>
                <PwaUpdateProvider isReloadDeferred={isRecoveryRoute(pathname)}>
                  <OrganizationProvider>
                    <AiChatContextProvider>
                      <RouterProvider router={router} />
                    </AiChatContextProvider>
                  </OrganizationProvider>
                  <PwaUpdateLayer />
                </PwaUpdateProvider>
              </BlockingInterfacesProvider>
            </OnboardingSessionProvider>
          </PwaInstallationProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;